// MARK://dhk8/admin-shell/pages/modules/StockItemDetailPage
// Ficha "ver-primero" de una existencia / stock item (Gustavo 2026-06-23). Header con
// nombre + almacén + SKU/insumo + pills (estado de stock OK/Bajo/Agotado, sin-costo) +
// KPIs (existencia, disponible, costo) + Tabs (General, Lotes, Movimientos, Historial).
// El tab General se ve primero; "Editar" abre el MISMO SectionedSheet que el modal de
// edición de InventoryPage (uom + reorderLevel + unitCostCents + presentaciones).
//
// Carga por id: NO existe inv.getStockItem(id), así que traemos inv.listInventory() y
// hacemos find(id) (mismo patrón que ProductDetailPage usa con las recetas). Los
// sub-recursos Lotes (inv.listLots) y Movimientos (inv.listMovements) traen TODO y se
// filtran por stockItemId — son los únicos sub-recursos con client; "Proveedores" se
// omite a propósito (SupplierItem se indexa por productId del catálogo, no por
// stockItemId, y vive en la ficha del Insumo).
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Plus, Trash2 } from 'lucide-react'

import * as inv from '../../lib/clients/inventory.js'
import type { InventoryItem } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import MoneyInput from '../../components/MoneyInput.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import DataTable, { type DataTableColumn } from '../../components/DataTable.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'lotes' | 'movimientos' | 'historial'

const STATUS_TONE: Record<string, StatusTone> = { OK: 'ok', LOW: 'warn', OUT: 'danger' }
const STATUS_LABEL: Record<string, string> = { OK: 'OK', LOW: 'Bajo', OUT: 'Agotado' }

interface EditForm {
  id: string
  name: string
  unit: string
  onHand: number
  reorderLevel: number
  unitCostCents: number
  status: InventoryItem['status']
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}
function fmtDay(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-MX', { dateStyle: 'medium' })
}

export default function StockItemDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()

  // No hay inv.getStockItem(id): traemos la lista y hacemos find(id).
  const list = useAsync((s) => inv.listInventory(s), [id])
  const warehouses = useAsync((s) => inv.listWarehouses(s))
  const lots = useAsync((s) => inv.listLots(s), [id])
  const movements = useAsync((s) => inv.listMovements(s), [id])

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<EditForm>({ id: '', name: '', unit: 'pza', onHand: 0, reorderLevel: 0, unitCostCents: 0, status: 'OK' })
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const item = useMemo(() => (list.data ?? []).find((it) => it.id === id) ?? null, [list.data, id])
  const whName = (wid: string | null) => (wid ? warehouses.data?.find((w) => w.id === wid)?.name ?? '—' : '—')

  const itemLots = useMemo(() => (lots.data ?? []).filter((l) => l.stockItemId === id), [lots.data, id])
  const itemMovements = useMemo(() => (movements.data ?? []).filter((m) => m.stockItemId === id), [movements.data, id])

  function openEdit() {
    if (!item) return
    setEditForm({ id: item.id, name: item.name, unit: item.unit, onHand: item.onHand, reorderLevel: item.reorderLevel, unitCostCents: item.unitCostCents ?? 0, status: item.status })
    setFormError(null); setEditing(true)
  }
  async function saveEdit() {
    setFormError(null); setSaving(true)
    try {
      await inv.updateItem({ id: editForm.id, name: editForm.name, unit: editForm.unit, onHand: editForm.onHand, reorderLevel: editForm.reorderLevel, status: editForm.status }, editForm.unitCostCents)
      setEditing(false); list.reload(); toast('Insumo actualizado', 'success')
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo actualizar el insumo.')
    } finally { setSaving(false) }
  }
  async function remove() {
    if (!item) return
    const ok = await confirm({ title: `¿Eliminar "${item.name}"?`, message: 'El insumo se quitará del inventario. No se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await inv.deleteItem(item.id); toast('Insumo eliminado', 'success'); navigate('/inventory') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar el insumo.', 'danger') }
  }

  const kpis = useMemo(() => {
    if (!item) return []
    const available = Math.round((item.onHand - item.reservedQty) * 100) / 100
    const items: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'danger' | 'info' | 'neutral' }[] = [
      { label: 'Existencia', value: `${item.onHand} ${unitShort(item.unit)}` },
      { label: 'Disponible', value: `${available} ${unitShort(item.unit)}`, tone: available <= 0 ? 'danger' : available < item.reorderLevel ? 'warn' : 'neutral' },
      { label: 'Costo', value: item.unitCostCents != null && item.unitCostCents > 0 ? mxn(item.unitCostCents) : 'Sin costo', tone: item.unitCostCents != null && item.unitCostCents > 0 ? 'neutral' : 'warn' },
    ]
    return items
  }, [item])

  const lotColumns: DataTableColumn<inv.StockLot>[] = [
    { key: 'lotNumber', header: 'Lote', render: (l) => <span style={{ fontWeight: 500 }}>{l.lotNumber}</span> },
    { key: 'serial', header: 'Serie', render: (l) => l.serialNumber || <span className="adm-muted">—</span> },
    { key: 'qty', header: 'Cantidad', align: 'right', render: (l) => <span className="adm-mono">{Math.round((l.qty - l.consumedQty) * 100) / 100}{l.uom ? ` ${unitShort(l.uom)}` : ''}</span> },
    { key: 'expiry', header: 'Caducidad', render: (l) => l.expiryDate ? <span className="adm-mono">{fmtDay(l.expiryDate)}</span> : <span className="adm-muted">—</span> },
    { key: 'warehouse', header: 'Almacén', render: (l) => whName(l.warehouseId) },
  ]

  const moveColumns: DataTableColumn<inv.StockMovement>[] = [
    { key: 'when', header: 'Cuándo', width: '170px', render: (m) => <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDate(m.createdAt)}</span> },
    { key: 'type', header: 'Tipo', render: (m) => <StatusPill tone={m.qty < 0 ? 'warn' : 'info'} label={m.type} /> },
    { key: 'qty', header: 'Cantidad', align: 'right', render: (m) => <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums', color: m.qty < 0 ? 'var(--pos-danger)' : 'var(--pos-fg)' }}>{m.qty > 0 ? '+' : ''}{m.qty}</span> },
    { key: 'reason', header: 'Motivo', render: (m) => m.reason || m.ref || <span className="adm-muted">—</span> },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/inventory')} aria-label="Volver a existencias"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Existencia</h1><p className="adm-page__sub">Inventario · ficha del insumo</p></div>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && !item && (
        <div className="adm-empty">No se encontró esta existencia. Puede haber sido eliminada.</div>
      )}

      {item && (
        <EntityDetailPage
          title={item.name}
          subtitle={`${whName(item.warehouseId)} · por ${unitShort(item.unit)}`}
          pills={<>
            <StatusPill tone={STATUS_TONE[item.status]} label={STATUS_LABEL[item.status]} />
            {(item.unitCostCents == null || item.unitCostCents <= 0) && <StatusPill tone="warn" label="Sin costo" />}
            {item.productId ? <StatusPill tone="neutral" label="Producto del catálogo" /> : <StatusPill tone="neutral" label="Insumo libre" />}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={<>
            <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={remove} aria-label="Eliminar"><Trash2 size={15} /></button>
          </>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'lotes', label: 'Lotes', count: itemLots.length },
              { id: 'movimientos', label: 'Movimientos', count: itemMovements.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones de la existencia"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Insumo / SKU">{item.name}</Field>
              <Field label="Almacén">{whName(item.warehouseId)}</Field>
              <Field label="Origen">{item.productId ? 'Producto del catálogo' : item.ingredientName ? 'Materia prima (nombre libre)' : '—'}</Field>
              <Field label="Existencia"><span className="adm-mono">{item.onHand} {unitShort(item.unit)}</span></Field>
              <Field label="Reservado"><span className="adm-mono">{item.reservedQty} {unitShort(item.unit)}</span></Field>
              <Field label="Disponible"><span className="adm-mono">{Math.round((item.onHand - item.reservedQty) * 100) / 100} {unitShort(item.unit)}</span></Field>
              <Field label="Mínimo (reorden)"><span className="adm-mono">{item.reorderLevel} {unitShort(item.unit)}</span></Field>
              <Field label="Costo unitario"><span className="adm-mono">{item.unitCostCents != null && item.unitCostCents > 0 ? mxn(item.unitCostCents) : <span className="adm-muted">Sin costo</span>}</span></Field>
              <Field label="Estado"><StatusPill tone={STATUS_TONE[item.status]} label={STATUS_LABEL[item.status]} /></Field>
              <p className="adm-row__meta" style={{ margin: 0 }}>Las existencias se ajustan desde la lista de existencias (botón “Ajustar a una cantidad exacta”).</p>
            </div>
          )}

          {tab === 'lotes' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(lots.loading, lots.error, lots.reload)}
              {lots.data && (
                <DataTable<inv.StockLot>
                  columns={lotColumns}
                  rows={itemLots}
                  getRowKey={(l) => l.id}
                  zebra
                  emptyText="Sin lotes registrados para esta existencia."
                />
              )}
            </div>
          )}

          {tab === 'movimientos' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(movements.loading, movements.error, movements.reload)}
              {movements.data && (
                <DataTable<inv.StockMovement>
                  columns={moveColumns}
                  rows={itemMovements}
                  getRowKey={(m) => m.id}
                  zebra
                  emptyText="Sin movimientos registrados para esta existencia."
                />
              )}
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={item.id} />}
        </EntityDetailPage>
      )}

      {/* Editar — MISMO formulario que el modal de InventoryPage (uom + mínimo + costo + presentaciones) */}
      <SectionedSheet
        open={editing}
        title="Editar insumo"
        onClose={() => { setEditing(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(false)} disabled={saving}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEdit} disabled={saving || !editForm.unit.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}

        <FormSection title="Identificación">
          <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={editForm.name} disabled aria-readonly="true" /></label>
        </FormSection>

        <FormSection title="Medida, costo y reorden">
          <UnitPicker label="Unidad" value={editForm.unit} onChange={(unit) => setEditForm({ ...editForm, unit })} />
          <label className="adm-field"><span className="adm-field__label">Costo unitario (MXN)</span><MoneyInput valueCents={editForm.unitCostCents} onChangeCents={(c) => setEditForm({ ...editForm, unitCostCents: c })} /></label>
          <label className="adm-field"><span className="adm-field__label">Mínimo</span><input className="adm-input" type="number" min={0} value={editForm.reorderLevel} onChange={(e) => setEditForm({ ...editForm, reorderLevel: Number(e.target.value) })} /></label>
        </FormSection>

        <FormSection title="Existencias (solo lectura)">
          <label className="adm-field"><span className="adm-field__label">Existencia actual</span><input className="adm-input" type="number" value={editForm.onHand} disabled aria-readonly="true" /></label>
          <p className="adm-row__meta">Las existencias se ajustan desde la lista (botón “Ajustar a una cantidad exacta”).</p>
        </FormSection>

        {item?.productId ? (
          <FormSection title="Presentaciones de compra">
            <PackPresentationsEditor productId={item.productId} baseUnit={editForm.unit} />
          </FormSection>
        ) : (
          <FormSection title="Presentaciones de compra">
            <p className="adm-row__meta">Disponible solo para insumos ligados a un producto del catálogo.</p>
          </FormSection>
        )}
      </SectionedSheet>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}

// ── editor de presentaciones de compra de un insumo ──────────────────────────
// Reubicado tal cual desde InventoryPage: Garrafón = 20000 (factor) × MLT (baseUnit),
// comprado por LTR (purchaseUnit/clave SAT). Mismos clients (listPackPresentations /
// upsertPackPresentation / deletePackPresentation).
function PackPresentationsEditor({ productId, baseUnit }: { productId: string; baseUnit: string }) {
  const confirm = useConfirm()
  const packs = useAsync((s) => inv.listPackPresentations(productId, s), [productId])
  const [form, setForm] = useState({ name: '', purchaseUnit: '', factor: 1, baseUnit })
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function openAdd() { setForm({ name: '', purchaseUnit: '', factor: 1, baseUnit }); setErr(null); setAdding(true) }
  async function add() {
    if (!form.name.trim() || !form.purchaseUnit.trim() || !form.baseUnit.trim() || form.factor <= 0) { setErr('Completa nombre, clave SAT de compra, factor (>0) y unidad base.'); return }
    setBusy(true); setErr(null)
    try { await inv.upsertPackPresentation({ productId, name: form.name.trim(), purchaseUnit: form.purchaseUnit.trim(), factor: form.factor, baseUnit: form.baseUnit.trim() }); setAdding(false); packs.reload() }
    catch (e) { setErr(e instanceof Error ? e.message : 'No se pudo guardar la presentación.') }
    finally { setBusy(false) }
  }
  async function remove(id: string) {
    const ok = await confirm({ title: '¿Eliminar presentación?', tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    await inv.deletePackPresentation(id).catch((e) => alert(e instanceof Error ? e.message : 'Error')); packs.reload()
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {asyncState(packs.loading, packs.error, packs.reload)}
      {(packs.data ?? []).length === 0 && !packs.loading && <p className="adm-row__meta">Sin presentaciones. Ej. "Garrafón" = 20000 MLT comprado por LTR.</p>}
      {(packs.data ?? []).map((p) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
          <span><strong>{p.name}</strong> · {unitShort(p.purchaseUnit)} → <span className="adm-mono">{p.factor}</span> {unitShort(p.baseUnit)}</span>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(p.id)} aria-label="Quitar presentación"><Trash2 size={13} /></button>
        </div>
      ))}
      {!adding ? (
        <button type="button" className="adm-btn adm-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={openAdd}><Plus size={14} /> Agregar presentación</button>
      ) : (
        <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 8 }}>
          {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}
          <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Garrafón, Caja…" /></label>
          <UnitPicker label="Unidad de compra" value={form.purchaseUnit} onChange={(purchaseUnit) => setForm({ ...form, purchaseUnit })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <label className="adm-field" style={{ flex: 1 }}><span className="adm-field__label">Factor (a unidad base)</span><input className="adm-input" type="number" min={0} step="any" value={form.factor} onChange={(e) => setForm({ ...form, factor: Number(e.target.value) })} placeholder="20000" /></label>
            <div style={{ flex: 1 }}><UnitPicker label="Unidad base" value={form.baseUnit} onChange={(b) => setForm({ ...form, baseUnit: b })} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="adm-btn adm-btn--sm" onClick={() => setAdding(false)} disabled={busy}>Cancelar</button>
            <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={add} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      )}
    </div>
  )
}
